import type {
  TrainingScript,
  LocalizedField,
  ChapterScript,
  SequenceScript,
  SubitemScript,
  MediaEntry,
  TranslationUpdatePatch,
  TranslationsByLocale,
  TrainingTranslationsPatch,
  ChapterTranslationsPatch,
  SequenceTranslationsPatch,
  SubitemTranslationsPatch,
  MediaTranslationsPatch
} from './types';
import { flattenSubitems } from './ScriptTranslationView.utils';

function fieldChanged(
  original?: LocalizedField | null,
  edited?: LocalizedField | null
): boolean {
  const originalTarget = (original?.target ?? '').trim();
  const editedTarget = (edited?.target ?? '').trim();
  return originalTarget !== editedTarget;
}

const oneLocale = (locale: string, value: string): TranslationsByLocale =>
  ({ [locale]: value } as TranslationsByLocale);

function upsertMediaPatch(
  patches: MediaTranslationsPatch[],
  mediaId: number
): MediaTranslationsPatch {
  let existing = patches.find(patch => patch.id === mediaId);
  if (!existing) {
    existing = { id: mediaId };
    patches.push(existing);
  }
  return existing;
}

export function buildTranslationsPayload(
  original: TrainingScript,
  edited: TrainingScript,
  targetLocale: string
): TranslationUpdatePatch | null {
  if (!targetLocale || targetLocale === 'en') return null;

  const patch: TranslationUpdatePatch = {};

  const trainingPatch: TrainingTranslationsPatch = {};
  if (fieldChanged(original.training.title, edited.training.title)) {
    const value = (edited.training.title?.target ?? '').trim();
    if (value)
      trainingPatch.title_translations = oneLocale(targetLocale, value);
  }
  if (
    fieldChanged(original.training.description, edited.training.description)
  ) {
    const value = (edited.training.description?.target ?? '').trim();
    if (value)
      trainingPatch.description_translations = oneLocale(targetLocale, value);
  }
  if (Object.keys(trainingPatch).length > 0) patch.training = trainingPatch;

  const originalChapterById = new Map(
    original.chapters.map(chapter => [chapter.id, chapter])
  );
  const originalSequenceById = new Map<number, SequenceScript>();
  const originalSubitemById = new Map<number, SubitemScript>();

  for (const chapter of original.chapters) {
    for (const sequence of chapter.sequences) {
      originalSequenceById.set(sequence.id, sequence);
      for (const subitem of flattenSubitems(sequence.subitems)) {
        originalSubitemById.set(subitem.id, subitem);
      }
    }
  }

  const chapterPatches: ChapterTranslationsPatch[] = [];
  for (const chapter of edited.chapters) {
    const baseChapter = originalChapterById.get(chapter.id);
    if (!baseChapter) continue;

    const chapterPatch: ChapterTranslationsPatch = { id: chapter.id };
    if (fieldChanged(baseChapter.title, chapter.title)) {
      const value = (chapter.title?.target ?? '').trim();
      if (value)
        chapterPatch.title_translations = oneLocale(targetLocale, value);
    }
    if (Object.keys(chapterPatch).length > 1) chapterPatches.push(chapterPatch);
  }
  if (chapterPatches.length > 0) patch.chapters = chapterPatches;

  const sequencePatchMap = new Map<number, SequenceTranslationsPatch>();

  for (const chapter of edited.chapters) {
    for (const sequence of chapter.sequences) {
      const baseSequence = originalSequenceById.get(sequence.id);
      if (!baseSequence) continue;

      let sequencePatch = sequencePatchMap.get(sequence.id);
      if (!sequencePatch) {
        sequencePatch = { id: sequence.id };
        sequencePatchMap.set(sequence.id, sequencePatch);
      }

      if (fieldChanged(baseSequence.title, sequence.title)) {
        const value = (sequence.title?.target ?? '').trim();
        if (value)
          sequencePatch.title_translations = oneLocale(targetLocale, value);
      }

      const baseMediaById = new Map(
        (baseSequence.media ?? []).map(mediaItem => [mediaItem.id, mediaItem])
      );
      const mediaPatches: MediaTranslationsPatch[] = sequencePatch.media ?? [];

      for (const mediaItem of sequence.media ?? []) {
        const baseMediaItem = baseMediaById.get(mediaItem.id);
        if (!baseMediaItem) continue;

        if (fieldChanged(baseMediaItem.script, mediaItem.script)) {
          const value = (mediaItem.script?.target ?? '').trim();
          if (value)
            upsertMediaPatch(mediaPatches, mediaItem.id).script_translations =
              oneLocale(targetLocale, value);
        }
        if (fieldChanged(baseMediaItem.name, mediaItem.name)) {
          const value = (mediaItem.name?.target ?? '').trim();
          if (value)
            upsertMediaPatch(mediaPatches, mediaItem.id).name_translations =
              oneLocale(targetLocale, value);
        }
      }
      if (mediaPatches.length > 0) sequencePatch.media = mediaPatches;

      const subitemPatches: SubitemTranslationsPatch[] =
        sequencePatch.subitems ?? [];

      for (const subitem of flattenSubitems(sequence.subitems)) {
        const baseSubitem = originalSubitemById.get(subitem.id);
        if (!baseSubitem) continue;

        const subitemPatch: SubitemTranslationsPatch = { id: subitem.id };

        if (fieldChanged(baseSubitem.title, subitem.title)) {
          const value = (subitem.title?.target ?? '').trim();
          if (value)
            subitemPatch.title_translations = oneLocale(targetLocale, value);
        }
        if (fieldChanged(baseSubitem.subtitle, subitem.subtitle)) {
          const value = (subitem.subtitle?.target ?? '').trim();
          if (value)
            subitemPatch.subtitle_translations = oneLocale(targetLocale, value);
        }
        if (fieldChanged(baseSubitem.body, subitem.body)) {
          const value = (subitem.body?.target ?? '').trim();
          if (value)
            subitemPatch.body_translations = oneLocale(targetLocale, value);
        }

        const subitemBaseMediaById = new Map(
          (baseSubitem.media ?? []).map(mediaItem => [mediaItem.id, mediaItem])
        );
        const subitemMediaPatches: MediaTranslationsPatch[] = [];

        for (const mediaItem of subitem.media ?? []) {
          const baseMediaItem = subitemBaseMediaById.get(mediaItem.id);
          if (!baseMediaItem) continue;

          if (fieldChanged(baseMediaItem.script, mediaItem.script)) {
            const value = (mediaItem.script?.target ?? '').trim();
            if (value)
              upsertMediaPatch(
                subitemMediaPatches,
                mediaItem.id
              ).script_translations = oneLocale(targetLocale, value);
          }
          if (fieldChanged(baseMediaItem.name, mediaItem.name)) {
            const value = (mediaItem.name?.target ?? '').trim();
            if (value)
              upsertMediaPatch(
                subitemMediaPatches,
                mediaItem.id
              ).name_translations = oneLocale(targetLocale, value);
          }
        }

        if (subitemMediaPatches.length > 0)
          subitemPatch.media = subitemMediaPatches;
        if (Object.keys(subitemPatch).length > 1)
          subitemPatches.push(subitemPatch);
      }

      if (subitemPatches.length > 0) sequencePatch.subitems = subitemPatches;
    }
  }

  const sequenceList = Array.from(sequencePatchMap.values()).filter(
    sequencePatch =>
      (sequencePatch.title_translations &&
        Object.keys(sequencePatch.title_translations).length > 0) ||
      (sequencePatch.subitems && sequencePatch.subitems.length > 0) ||
      (sequencePatch.media && sequencePatch.media.length > 0)
  );
  if (sequenceList.length > 0) patch.sequences = sequenceList;

  const hasTraining = patch.training && Object.keys(patch.training).length > 0;
  const hasChapters = patch.chapters && patch.chapters.length > 0;
  const hasSequences = patch.sequences && patch.sequences.length > 0;

  return hasTraining || hasChapters || hasSequences ? patch : null;
}
